"use client";

import { BlogCommentType, BlogCoverType } from "@v2/types/blog";
import { Avatar, Box, Button, colors, Divider, Fade, IconButton, NoSsr, Skeleton, Typography } from "@mui/material";
import api from "@v2/lib/api";
import axios, { PaginatedAxiosResponse } from "@v2/lib/axios";
import dayjs from "@v2/lib/dayjs";
import { useAppSelector } from "@v2/lib/redux/hooks";
import { useSnackbar } from "notistack";
import { ChevronDown, Heart, MessageSquare, User } from "lucide-react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import BlogAddCommentButton from "./BlogAddCommentButton";
import MediaV2 from "@v2/components/MediaV2";
import Logo from "@v2/components/Logo";

const PAGE_SIZE = 10;

export default function BlogComments({ postSlug, postTitle, postCover }: { postSlug: string; postTitle?: string; postCover?: BlogCoverType | null }) {
    const session = useAppSelector((s) => s.general.session);
    const isAuth = session?.isAuthenticated ?? false;
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : "");
    const signInHref = `/fa/sign-in?callback_url=${encodeURIComponent(`${currentPath}#comments`)}`;
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState<BlogCommentType[]>([]);
    const [pagination, setPagination] = useState<{ current_page: number; last_page: number; total: number } | null>(null);
    const [tempLikes, setTempLikes] = useState<Record<number, boolean>>({});

    const fetchComments = useCallback((page = 1) => {
        if (page === 1) setLoading(true);
        axios.get(`${api.blog_post_comments(postSlug)}?limit=${PAGE_SIZE}&page=${page}`)
            .then((res: PaginatedAxiosResponse) => {
                setLoading(false);
                // A 2xx body without a list (e.g. a CDN page) must not crash the whole page
                const items: BlogCommentType[] = Array.isArray(res.data?.data) ? res.data.data : [];
                setComments((prev) => page === 1 ? items : [...prev, ...items]);
                if (res.data?.pagination) setPagination(res.data.pagination);
            })
            .catch(() => setLoading(false));
    }, [postSlug]);

    useEffect(() => { fetchComments(1); }, [fetchComments]);

    const loadMore = useCallback(() => {
        if (pagination) fetchComments(pagination.current_page + 1);
    }, [pagination, fetchComments]);

    const toggleLike = useCallback((commentId: number, current: boolean) => () => {
        if (!isAuth) {
            enqueueSnackbar({
                message: (
                    <Box>
                        <Typography sx={{ fontSize: 14 }}>برای لایک ابتدا وارد حساب کاربری شوید</Typography>
                        <Button color="inherit" variant="outlined" component={Link} prefetch={false} href={signInHref} sx={{ mt: 1, minWidth: 68 }}>ورود</Button>
                    </Box>
                ),
            });
            return;
        }
        setTempLikes((prev) => ({ ...prev, [commentId]: !current }));
        axios.post(api.blog_comment_like, { comment_id: commentId });
    }, [isAuth, signInHref, enqueueSnackbar]);

    const fromNow = useMemo(() => (dt: string) => dayjs(dt).locale("fa").fromNow(), []);
    const onCommentAdded = useCallback(() => fetchComments(1), [fetchComments]);

    return (
        <NoSsr fallback={<LoadingFallback />}>
            <Box id="comments" sx={{ bgcolor: "#fff", p: 2, borderRadius: 2, mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, minHeight: 36 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MessageSquare size={20} color={colors.grey[700]} />
                        <Typography sx={{ fontSize: 18, fontWeight: 500, color: colors.grey[800] }}>
                            نظرات کاربران
                            {!!pagination?.total && (
                                <Typography component="span" sx={{ mr: 1, fontSize: 13, color: colors.grey[700] }}>({pagination.total})</Typography>
                            )}
                        </Typography>
                    </Box>
                    <BlogAddCommentButton postSlug={postSlug} postTitle={postTitle} postCover={postCover} onSuccess={onCommentAdded} />
                </Box>

                <Box>
                    {loading ? (
                        <LoadingSkeleton />
                    ) : comments.length === 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
                            <Typography sx={{ fontSize: 14, color: colors.grey[700] }}>تا کنون نظری برای این مقاله ثبت نشده است.</Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 500, color: colors.grey[800], mt: 1 }}>شما اولین نفر باشید</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {comments.map((comment, index) => (
                                <CommentItem
                                    key={comment.id}
                                    comment={comment}
                                    isLast={index === comments.length - 1}
                                    tempLikes={tempLikes}
                                    toggleLike={toggleLike}
                                    fromNow={fromNow}
                                    postSlug={postSlug}
                                    onReplyAdded={onCommentAdded}
                                />
                            ))}
                            {pagination && comments.length < pagination.total && (
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                    <Button color="inherit" variant="outlined" onClick={loadMore} endIcon={<ChevronDown />} sx={{ height: 48, minWidth: { xs: "100%", sm: 200 } }}>
                                        نمایش نظرات بیشتر
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </NoSsr>
    );
}

type CommentItemProps = {
    comment: BlogCommentType;
    depth?: number;
    isLast?: boolean;
    tempLikes: Record<number, boolean>;
    toggleLike: (id: number, current: boolean) => () => void;
    fromNow: (dt: string) => string;
    postSlug: string;
    onReplyAdded: () => void;
};

function CommentItem({ comment, depth = 0, isLast = true, tempLikes, toggleLike, fromNow, postSlug, onReplyAdded }: CommentItemProps) {
    const isLiked = tempLikes[comment.id] ?? comment.is_liked;
    const name = resolveName(comment);
    const likeCount = comment.likes + (tempLikes[comment.id] !== undefined ? (tempLikes[comment.id] ? 1 : -1) : 0);

    const avatarNode = (
        <Avatar sx={{ bgcolor: colors.grey[200], position: "relative", zIndex: depth === 0 ? 2 : 1, outline: "4px solid #fff", flexShrink: 0 }}>
            {comment.is_admin ? <Logo type="micro" /> : comment.user?.avatar ? (
                <MediaV2 media_id={String(comment.user.avatar.id)} alt={name} width={40} height={40} />
            ) : <User size={18} />}
        </Avatar>
    );

    const contentNode = (
        <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: colors.grey[900] }}>{name}</Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 1, my: 0.5 }} />
                <Typography component="span" sx={{ fontSize: 12, color: colors.grey[600] }}>{fromNow(comment.created_at)}</Typography>
            </Box>

            <Typography sx={{ fontSize: 14, color: colors.grey[800], textAlign: "justify", mt: 1.5, whiteSpace: "pre-line", lineHeight: 1.8 }}>
                {comment.description}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                <BlogAddCommentButton postSlug={postSlug} replyTo={comment} onSuccess={onReplyAdded} variant="text" />
                <IconButton onClick={toggleLike(comment.id, isLiked)} aria-label={isLiked ? "برداشتن لایک" : "لایک کردن"} sx={{ bgcolor: "#fff", height: 32, width: 32 }}>
                    {isLiked ? <Heart size={15} fill={colors.red[700]} color={colors.red[700]} /> : <Heart size={15} color={colors.grey[600]} />}
                </IconButton>
                {(comment.likes > 0 || tempLikes[comment.id] !== undefined) && (
                    <Fade in>
                        <Typography sx={{ fontSize: 12, color: colors.grey[700] }}>{likeCount}</Typography>
                    </Fade>
                )}
            </Box>

            {comment.children?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {comment.children.map((child) => (
                        <CommentItem
                            key={child.id}
                            comment={child}
                            depth={depth + 1}
                            tempLikes={tempLikes}
                            toggleLike={toggleLike}
                            fromNow={fromNow}
                            postSlug={postSlug}
                            onReplyAdded={onReplyAdded}
                        />
                    ))}
                </Box>
            )}

            {depth === 0 && !isLast && <Divider flexItem sx={{ my: 2 }} />}
        </Box>
    );

    if (depth === 0) {
        return (
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1, gap: 1, position: "relative", overflow: "hidden" }}>
                {avatarNode}
                {contentNode}
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
            <Box sx={{
                position: "relative",
                height: 40,
                flexShrink: 0,
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: -30,
                    bottom: "50%",
                    height: "240px",
                    width: 30,
                    borderLeft: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: colors.grey[200],
                    borderRadius: "0 0 0 8px",
                },
            }}>
                {avatarNode}
            </Box>
            {contentNode}
        </Box>
    );
}

function resolveName(c: BlogCommentType): string {
    if (c.is_unknown) return "کاربر ناشناس";
    if (c.is_admin) return "تیم وحدت";
    if (c.user) return c.user.title_name || [c.user.first_name, c.user.last_name].filter(Boolean).join(" ") || "کاربر";
    return "کاربر مهمان";
}

function LoadingFallback() {
    return (
        <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: 2, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 2 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={120} height={28} />
            </Box>
            <Skeleton variant="rounded" width="100%" height={150} />
        </Box>
    );
}

function LoadingSkeleton() {
    return <Skeleton variant="rounded" width="100%" height={150} />;
}
